import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { handleApiCall } from "../apis";
import { endpoints } from "../utils/endpoints";
import { UI_CONSTANTS } from "../utils/constants";
import { useLanguage } from "../context/LanguageContext";
import { getLocalizedFieldValue } from "../utils/language";
import he from "he";

const slugify = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-");

const buildMediaUrl = (path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
  if (!base) return path;
  const normalizedBase = base.replace(/\/v1$/i, "");
  return `${normalizedBase}/${path.replace(/^\/+/, "")}`;
};

const truncateText = (text = "", maxLength = 160) => {
  if (!text) return "";
  const decoded = he.decode(text);
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = decoded;
  const plainText = tempDiv.textContent || tempDiv.innerText || "";
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength).trim() + "...";
};

const normalizeNewsData = (data) => {
  if (!data) return [];
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.news)) return data.news;
  if (Array.isArray(data)) return data;
  return [];
};

const CARDS_PER_PAGE = 4;

const isVideoItem = (item) => {
  const type = (item?.type || "").toLowerCase();
  const media = item?.desktop_news || "";
  return type === "video" || /\.(mp4|webm|ogg|mov)(\?|$)/i.test(media);
};

function LatestContent() {
  const { setBannerData } = useOutletContext();
  const { language } = useLanguage();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(CARDS_PER_PAGE);
  const isLoadingMore = useRef(false);
  const visibleCountRef = useRef(CARDS_PER_PAGE);
  const newsLengthRef = useRef(0);

  visibleCountRef.current = visibleCount;
  newsLengthRef.current = newsItems.length;

  useEffect(() => {
    setBannerData({
      image: null,
      MobileBanner: null,
      title: "",
      subtitle: "",
      skew: false,
      bannershow: false,
      titleClass: "",
    });
  }, [setBannerData]);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await handleApiCall("get", endpoints.newsList);
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true ||
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS
      ) {
        const items = normalizeNewsData(response?.data?.data);
        setNewsItems(items);
        setVisibleCount(CARDS_PER_PAGE);
      } else {
        setNewsItems([]);
        setVisibleCount(CARDS_PER_PAGE);
      }
    } catch (error) {
      console.error("Error fetching news list:", error);
      setNewsItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  useEffect(() => {
    const handleScroll = () => {
      if (isLoadingMore.current) return;
      if (visibleCountRef.current >= newsLengthRef.current) return;

      const scrollBottom = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.scrollHeight;
      if (scrollBottom < pageHeight - 240) return;

      isLoadingMore.current = true;
      setVisibleCount((count) =>
        Math.min(count + CARDS_PER_PAGE, newsLengthRef.current)
      );
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    isLoadingMore.current = false;
  }, [visibleCount]);

  const visibleItems = newsItems.slice(0, visibleCount);
  const hasMore = visibleCount < newsItems.length;

  const openItem = (item) => {
    const itemId = item?._id || item?.id;
    if (!itemId) return;

    if (isVideoItem(item)) {
      const title = getLocalizedFieldValue(item, language, "title") || "";
      window.open(`/video/${itemId}/${slugify(title)}`, "_blank", "noopener,noreferrer");
      return;
    }

    window.open(`/news/${itemId}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="container max-w-8xl mx-auto px-0 py-8 md:py-0 md:px-8 2xl:px-32">
      <h2
        className={`text-white mb-8 md:mb-12 mt-20 ${
          language === "AR"
            ? "text-[22px] md:text-[2rem] lg:text-[2.625rem] font-SaudiaBold"
            : "text-[28px] md:text-[2rem] lg:text-[2.625rem] font-SaudiaBoldItalic"
        }`}
      >
        {language === "AR" ? "آخر الأخبار والمحتوى" : "Latest news & content"}
      </h2>

      {loading ? (
        <p className="text-white text-center py-16">
          {UI_CONSTANTS.BUTTON_LABELS.LOADING || "Loading..."}
        </p>
      ) : newsItems.length === 0 ? (
        <p className="text-white text-center py-16">
          {language === "AR" ? "لا توجد بيانات متاحة حالياً." : UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
        </p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-5 lg:gap-10 skewSlider">
          {visibleItems.map((item, index) => {
            const title =
              getLocalizedFieldValue(item, language, "title") ||
              UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;
            const description = truncateText(
              getLocalizedFieldValue(item, language, "discription") || "",
              160
            );
            const isVideo = isVideoItem(item);
            const mediaUrl = buildMediaUrl(item?.desktop_news);
            const isLeftCard = language === "AR" ? index % 2 === 1 : index % 2 === 0;
            const buttonLabel = isVideo
              ? language === "AR"
                ? "شاهد الآن"
                : UI_CONSTANTS.BUTTON_LABELS.PLAY_NOW || "Play now"
              : language === "AR"
                ? "اقرأ المزيد"
                : UI_CONSTANTS.BUTTON_LABELS.READ_MORE || "Read more";

            return (
              <article
                key={item?._id || item?.id || index}
                className={`relative ${
                  isLeftCard ? "is-left" : "is-right"
                }`}
              >
                <div className="relative h-[180px] md:h-[270px] lg:h-[334px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
                  {mediaUrl ? (
                    isVideo ? (
                      <video
                        className="absolute inset-0 w-full h-full object-cover"
                        src={mediaUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls={false}
                        preload="metadata"
                      />
                    ) : (
                      <picture className="absolute inset-0 w-full h-full">
                        <source media="(min-width: 768px)" srcSet={item.desktop_news} />
                        <img
                          src={item.mobile_news || item.desktop_news}
                          alt={title}
                          className="w-full h-full object-cover"
                        />
                      </picture>
                    )
                  ) : (
                    <div className="absolute inset-0 bg-neutral-800" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80"></div>
                </div>
                <div
                  className={`absolute inset-x-0 bottom-0 z-10 px-4 pb-5 md:px-6 md:pb-7 ${
                    language === "AR" ? "text-right" : "text-left"
                  }`}
                >
                  <h3 className="text-white text-xl md:text-[1.75rem] font-SaudiaBold leading-tight">
                    {title}
                  </h3>
                  {description ? (
                    <p className="text-white/90 mt-2 text-xs md:text-base font-SaudiaLight leading-snug line-clamp-3 md:max-w-[90%]">
                      {description}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => openItem(item)}
                    className="mt-3 md:mt-4 inline-flex items-center justify-center bg-saudiaGreen text-white rounded-md w-[7.5rem] md:w-[8.875rem] h-9 md:h-11 text-xs md:text-lg font-SaudiaLight"
                  >
                    {buttonLabel}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      {hasMore && !loading ? <div className="h-16" /> : null}
    </div>
  );
}

export default LatestContent;
